<div class="back-row">
  <a class="back-link" routerLink="/projects">
    <mat-icon>arrow_back</mat-icon>
    Back to Projects
  </a>
  <!-- AI processes running on this project, if any. -->
  <app-ai-activity-chip [reviews]="runningAiReviews()" />
</div>

@if (id()) {
  @let av = avatarFor(id());
  <!--
    The layout renders at once: everything except the header and the mockups only
    needs the round id (from the route), so it starts loading in parallel; the
    header shows placeholders until the round itself arrives.
  -->
  <!-- The header and the project team side by side, half each. -->
  <div class="detail-row">
    @if (round(); as project) {
      <div class="project-strip">
        <span class="strip-avatar" [style.background-color]="av.color">
          <mat-icon>{{ av.icon }}</mat-icon>
        </span>

        <div class="strip-main">
          <div class="project-name">{{ project.projectName || project.projectId }}</div>
          <div class="id-grid">
            <span class="id-key">Org ID</span>
            <span class="id-val">{{ project.orgId }}</span>
            <span class="id-key">Project ID</span>
            <span class="id-val">{{ project.projectId }}</span>
            <span class="id-key">Feedback Round ID</span>
            <span class="id-val">{{ project.id }}</span>
          </div>
        </div>

        <div class="strip-meta">
          <div class="strip-actions">
            <fb-status-chip
              [statuses]="statuses"
              [current]="project.status"
              (statusChange)="onStatusChange($event)"
            />
            <button
              class="strip-settings"
              type="button"
              (click)="manualEmailOpen.set(true)"
              aria-label="Send an email"
              title="Send an email — Pages Ready for Review"
            >
              <mat-icon>mail</mat-icon>
            </button>
            <button
              class="strip-settings"
              type="button"
              (click)="onOpenEmbedSettings()"
              aria-label="Embed settings"
              title="Embed settings — allowed domains"
            >
              <mat-icon>code</mat-icon>
            </button>
          </div>

          <div class="progress-block">
            <div class="progress-head">
              <span class="progress-label">Progress</span>
              <span class="progress-pct">{{ project.progress ?? 0 }}%</span>
            </div>
            <fb-progress-bar [value]="project.progress ?? 0" [height]="5" />
          </div>
        </div>
      </div>
    } @else if (roundLoading()) {
      <!-- Header placeholder: the round id is already known from the route. -->
      <div class="project-strip" aria-busy="true">
        <span class="strip-avatar" [style.background-color]="av.color">
          <mat-icon>{{ av.icon }}</mat-icon>
        </span>

        <div class="strip-main">
          <span class="placeholder-bar placeholder-name"></span>
          <div class="id-grid">
            <span class="id-key">Org ID</span>
            <span class="placeholder-bar placeholder-id"></span>
            <span class="id-key">Project ID</span>
            <span class="placeholder-bar placeholder-id"></span>
            <span class="id-key">Feedback Round ID</span>
            <span class="id-val">{{ id() }}</span>
          </div>
        </div>

        <div class="strip-meta">
          <span class="strip-loading" role="status">Loading project…</span>
          <fb-loading-bar class="strip-loading-bar" [height]="5" />
        </div>
      </div>
    } @else {
      <div class="project-strip strip-error" role="alert">
        <mat-icon>error_outline</mat-icon>
        <span>This project couldn't be loaded. Refresh to try again.</span>
      </div>
    }

    <app-stakeholders-panel
      [stakeholders]="stakeholders()"
      [pictureUrls]="teamPictureUrls()"
      [loading]="stakeholdersLoading()"
      [busy]="stakeholdersBusy()"
      (add)="onAddStakeholder($event)"
      (remove)="onRemoveStakeholder($event)"
      (roleChange)="onStakeholderRoleChange($event)"
      (memberSelect)="preferencesMember.set($event)"
    />
  </div>

  <!-- Mockups and pages side by side, half each. -->
  <div class="detail-row">
    <app-mockups-panel
      [mockups]="round()?.mockupUrls ?? []"
      [assets]="round()?.assets ?? []"
      [loading]="roundLoading()"
      [removingAssetIds]="removingAssetIds()"
      (upload)="onUploadMockups()"
      (addAsset)="addAssetOpen.set(true)"
      (removeAsset)="onRemoveAsset($event)"
    />

    <app-pages-panel
      [feedbackRoundId]="id()"
      [mockups]="round()?.mockupUrls ?? []"
      [statuses]="pageStatuses"
      (roundUpdated)="onRoundPagesUpdated($event)"
      (pagesChanged)="onPagesChanged()"
    />
  </div>

  <fb-feedback-table
    [items]="feedbackRows()"
    [count]="feedbackTotal()"
    [page]="feedbackCurrentPage()"
    [perPage]="feedbackPerPage()"
    [loading]="feedbackLoading()"
    [sort]="sort()"
    [filters]="filters()"
    [categoryOptions]="categoryFilterOptions"
    [urlOptions]="feedbackUrls()"
    [userOptions]="feedbackUserOptions()"
    [statuses]="feedbackStatuses"
    [selectable]="true"
    [pageLink]="pageReviewLink"
    [(selectedIds)]="selectedFeedbackIds"
    (bulkStatusChange)="onBulkStatusChange($event)"
    (sortChange)="sort.set($event)"
    (filtersChange)="filters.set($event)"
    (pageChange)="feedbackPage.set($event)"
    (openFeedback)="onOpenFeedback($event)"
    (statusChange)="onRowStatusChange($event)"
    (newFeedback)="onNewFeedback()"
    (exportFeedback)="onExportFeedback()"
  />

  <app-email-log-panel [feedbackRoundId]="id()" [refreshKey]="emailLogRefresh()" />
}

<app-manual-email-dialog
  [open]="manualEmailOpen()"
  [feedbackRoundId]="round()?.id ?? ''"
  [stakeholders]="stakeholders()"
  [sender]="emailSender()"
  (sent)="onManualEmailSent($event)"
  (close)="manualEmailOpen.set(false)"
/>

<app-stakeholder-preferences-dialog
  [feedbackRoundId]="round()?.id ?? ''"
  [member]="preferencesMember()"
  (close)="preferencesMember.set(null)"
/>

<fb-disregard-reason
  [open]="disregardPending() !== null || bulkDisregardPending() !== null"
  [itemLabel]="disregardItemLabel()"
  (confirm)="onDisregardReasonConfirmed($event)"
  (cancel)="onDisregardCancelled()"
/>

<fb-export-feedback
  [open]="exportOpen()"
  [visibleCount]="feedbackTotal()"
  [totalCount]="feedbackTotal()"
  [filterChips]="exportFilterChips()"
  (exportFormat)="onExportFormat($event)"
  (close)="exportOpen.set(false)"
/>

<!--
  canPostPrivateComment is true here because this is the staff-facing app; the
  widget leaves it off, since the API refuses private comments from a client token.
-->
<fb-feedback-detail
  [feedback]="selectedFeedback()"
  [statuses]="feedbackStatuses"
  [comments]="commentThread()"
  [commentsLoading]="commentsLoading()"
  [commentPosting]="commentPosting()"
  [canPostPrivateComment]="true"
  [commentRecipientOptions]="commentRecipientOptions()"
  [commentRecipientAuthorEmail]="commentRecipientAuthorEmail()"
  [currentUserEmail]="currentUserEmail()"
  [pageLink]="pageReviewLink"
  (statusChange)="onFeedbackStatusChange($event)"
  (commentPost)="onPostComment($event)"
  (close)="selectedFeedback.set(null)"
/>

<fb-new-feedback
  [open]="newFeedbackOpen()"
  [submitting]="creatingFeedback()"
  (submit)="onSubmitNewFeedback($event)"
  (close)="onCloseNewFeedback()"
/>

<app-upload-mockups-drawer
  [open]="uploadMockupsOpen()"
  [feedbackRoundId]="round()?.id ?? ''"
  (uploaded)="onMockupsUploaded($event)"
  (close)="uploadMockupsOpen.set(false)"
/>

<app-add-asset-drawer
  [open]="addAssetOpen()"
  [feedbackRoundId]="round()?.id ?? ''"
  (added)="onAssetAdded($event)"
  (close)="addAssetOpen.set(false)"
/>

<app-embed-settings-drawer
  [open]="embedSettingsOpen()"
  [orgId]="round()?.orgId ?? ''"
  [projectId]="round()?.projectId ?? ''"
  [domains]="round()?.allowedDomains ?? []"
  [saving]="savingDomains()"
  (save)="onSaveAllowedDomains($event)"
  (close)="embedSettingsOpen.set(false)"
/>

<fb-ai-assistant [prompts]="aiPrompts()" (runPrompt)="onAiReview($event)" />
